<script>
import { defineComponent, nextTick, createVNode, h, ref, compile } from 'vue';
// Import runtime template compilation function - use specific compiler import for production compatibility
// Note: We'll use Vue's runtime compiler from the full build instead of compile-time compiler
import * as Vue from 'vue';
import { motion, AnimatePresence } from 'motion-v';
import WidgetMixin from '../../Mixins/WidgetMixin';
import MotionLoader from './MotionLoader.vue';
import axios from 'axios';
import videojs from 'video.js';

// Import our new template renderer utility
import { 
  getCompileFunction, 
  testCompileFunction, 
  processHtmlForReactivity, 
  createTemplateRenderer 
} from '../Utils/TemplateRenderer.js';

export default defineComponent({
  name: 'CustomWidget',
  mixins: [WidgetMixin],
  components: {
    motion,
    AnimatePresence,
    MotionLoader
  },
  data() {
    return {
      // simple fields
      textfield: null,
      textfield2: null,
      textfield3: null,
      textfield4: null,
      textfield5: null,
      textfield6: null,
      textfield7: null,
      currentTab: null,

      // object & array fields
      objectfield1: {},
      objectfield2: {},
      objectfield3: {},
      objectfield4: {},
      objectfield5: {},
      objectfield6: {},
      objectfield7: {},
      arrayfield1: [],
      arrayfield2: [],
      arrayfield3: [],
      arrayfield4: [],
      arrayfield5: [],
      arrayfield6: [],
      arrayfield7: [],

      // custom variables & webhooks
      customVariables: {},
      triggerWebhooks: [],

      // animation control
      isVisible: true,
      animationKey: 0,
      animationPreset: 'default', // 'default', 'slide', 'fade', 'zoom', 'flip', 'elegant'
      loaderVariant: null,
      loaderSize: null,
      hasInitiallyLoaded: false, // Track if component has loaded initially
      contentKey: 0, // Separate key for content updates without animation
    };
  },
  computed: {
    animationVariants() {
      const presets = {
        default: {
          initial: { 
            opacity: 0, 
            y: 20, 
            scale: 0.95,
            filter: 'blur(10px)'
          },
          animate: { 
            opacity: 1, 
            y: 0, 
            scale: 1,
            filter: 'blur(0px)'
          },
          exit: { 
            opacity: 0, 
            y: -20, 
            scale: 1.05,
            filter: 'blur(5px)'
          },
          transition: {
            type: 'spring',
            stiffness: 300,
            damping: 30,
            mass: 0.8,
            opacity: { duration: 0.3 },
            filter: { duration: 0.3 }
          }
        },
        slide: {
          initial: { opacity: 0, x: -100 },
          animate: { opacity: 1, x: 0 },
          exit: { opacity: 0, x: 100 },
          transition: { type: 'spring', stiffness: 400, damping: 25 }
        },
        fade: {
          initial: { opacity: 0 },
          animate: { opacity: 1 },
          exit: { opacity: 0 },
          transition: { duration: 0.5, ease: 'easeInOut' }
        },
        zoom: {
          initial: { opacity: 0, scale: 0.3 },
          animate: { opacity: 1, scale: 1 },
          exit: { opacity: 0, scale: 0.8 },
          transition: { type: 'spring', stiffness: 500, damping: 25 }
        },
        flip: {
          initial: { opacity: 0, rotateY: -90 },
          animate: { opacity: 1, rotateY: 0 },
          exit: { opacity: 0, rotateY: 90 },
          transition: { type: 'spring', stiffness: 300, damping: 30 }
        },
        elegant: {
          initial: { 
            opacity: 0, 
            y: 30, 
            scale: 0.9,
            filter: 'blur(20px)',
            rotateX: -10
          },
          animate: { 
            opacity: 1, 
            y: 0, 
            scale: 1,
            filter: 'blur(0px)',
            rotateX: 0
          },
          exit: { 
            opacity: 0, 
            y: -30, 
            scale: 0.95,
            filter: 'blur(10px)',
            rotateX: 5
          },
          transition: {
            type: 'spring',
            stiffness: 200,
            damping: 20,
            mass: 1,
            opacity: { duration: 0.4 },
            filter: { duration: 0.4 }
          }
        }
      };
      return presets[this.animationPreset] || presets.default;
    },

    // Computed property for reactive HTML processing
    processedHtml() {
      // Cache the result to prevent infinite loops
      const cacheKey = `${this.html}-${this.contentKey}`;
      if (this._processedHtmlCache && this._processedHtmlCache.key === cacheKey) {
        return this._processedHtmlCache.value;
      }
      
      // Create evaluation context
      const context = {
        ...this.$data,
        // Add common methods
        updateProperty: this.updateProperty,
        refreshAnimation: this.refreshAnimation,
        setAnimationPreset: this.setAnimationPreset,
        getCustomVariable: this.getCustomVariable,
        setCustomVariable: this.setCustomVariable,
        getCustomVariableDelayed: this.getCustomVariableDelayed,
        // Add webhook and validation methods
        triggerWebhook: this.triggerWebhook,
        isValidEmail: this.isValidEmail,
        // Add date/time helpers
        Date: Date,
        moment: window.moment || (() => new Date()),
        // Add utility functions
        nl2br: this.nl2br,
        translateField: this.translateField,
        translate: this.translate
      };
      
      const result = processHtmlForReactivity(this.html, context);
      
      // Cache the result
      this._processedHtmlCache = {
        key: cacheKey,
        value: result
      };
      
      return result;
    },

    // Animation variants for initial load only
    initialAnimationVariants() {
      if (this.hasInitiallyLoaded) {
        // After initial load, use no animation for content updates
        return {
          initial: { opacity: 1, y: 0, scale: 1, filter: 'blur(0px)' },
          animate: { opacity: 1, y: 0, scale: 1, filter: 'blur(0px)' },
          exit: { opacity: 1, y: 0, scale: 1, filter: 'blur(0px)' },
          transition: { duration: 0 }
        };
      }
      // Use full animation for initial load
      return this.animationVariants;
    },

    // Stable key for content that doesn't trigger re-animation
    stableContentKey() {
      // Only change when animationKey changes (manual refresh) or initial load
      return `content-${this.animationKey}`;
    }
  },
  methods: {
    triggerWebhook(url) {
      if (this.triggerWebhooks.includes(url)) {
        return '';
      }
      this.triggerWebhooks.push(url);

      axios.get(url)
          .then(response => console.log(response))
          .catch(error => console.log(error));

      return '';
    },

    isValidEmail(email) {
      return !!email && /[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
    },

    getCustomVariable(key, defaultValue = false) {
      if (!(key in this.customVariables)) {
        this.customVariables[key] = defaultValue;
      }
      return this.customVariables[key];
    },

    getCustomVariableDelayed(key, startValue, delay, endValue) {
      if (!(key in this.customVariables)) {
        this.customVariables[key] = startValue;
        setTimeout(() => {
          this.customVariables[key] = endValue;
        }, delay);
      }
      return this.customVariables[key];
    },

    setCustomVariable(key, value) {
      console.log('setCustomVariable', key, value);
      console.log('this.customVariables', this.customVariables);
      this.customVariables[key] = value;
    },

    htmlLoadedCustom() {
      // initialize video.js players if any
      const players = document.querySelectorAll('.videoPlayer');
      if (players.length) {
        videojs('.videoPlayer', this.playerOptions);
      }

      // your TAOS animation bootstrapping
      nextTick(() => {
        (function () {
          const debounce = (fn, d) => {
            let id = null;
            return () => {
              clearTimeout(id);
              id = setTimeout(fn, d);
            };
          };
          const throttle = (fn, d) => {
            let busy = false;
            return () => {
              if (!busy) {
                fn();
                busy = true;
                setTimeout(() => busy = false, d);
              }
            };
          };
          const reset = el => {
            if (el.className !== el.dataset.taosClass) {
              el.className = el.dataset.taosClass;
            }
          };
          const before = el => el.className = el.className.replaceAll('taos:', '');

          const initElement = el => {
            if (!el.className.includes('taos-init')) {
              el.dataset.taosClass = el.className + ' taos-init';
              reset(el);
            }
            el.className += ' !duration-[0ms] !delay-[0ms]';
            before(el);
            return {
              element: el,
              once: getComputedStyle(el)['animation-iteration-count'] === '1',
              offset: parseInt(el.dataset.taosOffset || 0, 10),
            };
          };

          let elements = [];
          let innerW = window.innerWidth;
          let scrollY = window.scrollY;

          const refreshTriggers = throttle(() => {
            elements.forEach(o => {
              o.trigger = o.element.getBoundingClientRect().top - window.innerHeight + o.offset + scrollY;
            });
          }, 250);

          const refresh = () => {
            elements = [];
            document.querySelectorAll('[class*="taos"]').forEach(el => {
              elements.push(initElement(el));
            });
            refreshTriggers();
            requestAnimationFrame(handleScroll);
          };

          const handleScroll = () => {
            scrollY = window.scrollY;
            elements.forEach(({ element, trigger, once }) => {
              if (trigger < scrollY) {
                reset(element);
              } else if (!once && element.className.includes('taos:')) {
                before(element);
              }
            });
            refreshTriggers();
          };

          const handleResize = () => {
            if (innerW !== window.innerWidth) {
              innerW = window.innerWidth;
              refresh();
            }
          };

          refresh();
          addEventListener('scroll', throttle(handleScroll, 32));
          addEventListener('orientationchange', refresh);
          addEventListener('resize', debounce(handleResize, 250));

          const observer = new MutationObserver(muts => {
            muts.forEach(({ target }) => {
              if (
                  target.className &&
                  !target.className.includes('taos-init') &&
                  target.className.includes('taos:')
              ) {
                elements.push(initElement(target));
              }
            });
          });
          observer.observe(document, { attributes: true, childList: true, subtree: true });
        })();
      });
    },

    nl2br(str, is_xhtml) {
      if (str == null) return '';
      const br = is_xhtml || is_xhtml == null ? '<br />' : '<br>';
      return ('' + str).replace(/([^>\r\n]?)(\r\n|\n\r|\r|\n)/g, `$1${br}$2`);
    },

    updateCurrentTab(newTab) {
      this.currentTab = newTab;
    },

    updateProperty(propertyName, value) {
      // Uncomment for debugging:
      // console.log('In updateProperty. this:', this, 'propertyName:', propertyName, 'value:', value);

      if (typeof this !== 'object' || this === null) {
        console.error('updateProperty was called with an invalid "this" context:', this);
        return;
      }

      // Ensure $data is available, as we intend to modify data properties
      if (typeof this.$data !== 'object' || this.$data === null) {
        console.error('updateProperty: "this.$data" is not accessible or not an object. "this" context:', this);
        return;
      }

      if (Object.prototype.hasOwnProperty.call(this.$data, propertyName)) {
        this[propertyName] = value; // Vue will handle reactivity for properties in $data
        
        // Increment content key to trigger re-render without animation
        this.contentKey += 1;
        
        // Note: No automatic animation refresh - content updates should be smooth
      } else {
        // Property not found in $data.
        // It could be a prop (which shouldn't be mutated here), a method,
        // or a non-reactive property.
        console.warn(`Property '${propertyName}' not found in the component's $data. Ensure it is declared in the data() option for reactivity. If it's a prop, it should not be mutated directly.`);
      }
    },

    refreshAnimation() {
      // Increment the animation key to trigger a smooth re-render with motion
      // Reset hasInitiallyLoaded to allow animation on manual refresh
      this.hasInitiallyLoaded = false;
      this.animationKey += 1;
      this.contentKey += 1;
    },

    triggerContentUpdate() {
      // Smooth content update animation
      this.isVisible = false;
      this.$nextTick(() => {
        this.isVisible = true;
        this.refreshAnimation();
      });
    },

    setAnimationPreset(preset) {
      // Change animation style: 'default', 'slide', 'fade', 'zoom', 'flip', 'elegant'
      this.animationPreset = preset;
      this.refreshAnimation();
    },

    animateIn() {
      // Manual trigger for entrance animation
      this.isVisible = true;
      this.refreshAnimation();
    },

    animateOut() {
      // Manual trigger for exit animation
      this.isVisible = false;
    },

    testLoader() {
      // Test method to manually show loader
      this.isLoading = true;
      setTimeout(() => {
        this.isLoading = false;
      }, 2000);
    },

    forceRefreshAnimation() {
      // Force a re-animation regardless of HTML changes
      // Use this when you know the content has changed significantly
      console.log('Forcing component re-animation');
      this.refreshAnimation();
    },

    updatePropertyWithAnimation(propertyName, value) {
      // Alternative to updateProperty that WILL trigger re-animation
      // Use this when you want the property update to also re-animate the component
      this.updateProperty(propertyName, value);
      this.refreshAnimation();
    },

    isSignificantHtmlChange(oldHtml, newHtml) {
      // Helper function to extract structural elements (tags, classes, major structure)
      const extractStructure = (html) => {
        if (!html) return '';
        
        // Remove dynamic content that changes with user interactions
        // Keep only structural elements, classes, and major layout
        return html
          .replace(/>\s*[^<]+\s*</g, '><')  // Remove text content between tags
          .replace(/value="[^"]*"/g, 'value=""')  // Remove dynamic values
          .replace(/data-\w+="[^"]*"/g, '')  // Remove dynamic data attributes
          .replace(/style="[^"]*"/g, '')     // Remove dynamic styles
          .replace(/\s+/g, ' ')              // Normalize whitespace
          .trim();
      };
      
      const oldStructure = extractStructure(oldHtml);
      const newStructure = extractStructure(newHtml);
      
      // Only consider it significant if the structure actually changed
      const hasStructuralChange = oldStructure !== newStructure;
      
      // Additional check: if the length difference is significant (> 20%), it's likely structural
      const lengthDiffPercent = Math.abs(newHtml.length - oldHtml.length) / oldHtml.length;
      const hasSignificantLengthChange = lengthDiffPercent > 0.2;
      
      return hasStructuralChange || hasSignificantLengthChange;
    },

    // Use the utility function instead of the local implementation
    testCompileFunction,

    // Add utility methods that are still referenced
    evaluateExpression(expression, context) {
      // Import the utility function dynamically or use a simple fallback
      // For now, we'll use a simple property access
      if (context.hasOwnProperty(expression)) {
        return context[expression];
      }
      // Remove quotes if present
      const cleanExpression = expression.replace(/['"]/g, '');
      return cleanExpression;
    },

    parseComplexArguments(argsString) {
      if (!argsString.trim()) return [];
      
      const shouldLog = argsString.length > 50 || argsString.includes('\\u');
      if (shouldLog) {
        console.log('Parsing complex arguments:', argsString.substring(0, 100) + (argsString.length > 100 ? '...' : ''));
      }
      
      const args = [];
      let current = '';
      let inQuotes = false;
      let quoteChar = '';
      let depth = 0;
      let i = 0;
      
      while (i < argsString.length) {
        const char = argsString[i];
        
        if (!inQuotes && (char === '"' || char === "'")) {
          inQuotes = true;
          quoteChar = char;
          current += char;
        } else if (inQuotes && char === quoteChar) {
          let escapeCount = 0;
          let j = i - 1;
          while (j >= 0 && argsString[j] === '\\') {
            escapeCount++;
            j--;
          }
          
          if (escapeCount % 2 === 0) {
            inQuotes = false;
            current += char;
          } else {
            current += char;
          }
        } else if (!inQuotes && char === '(') {
          depth++;
          current += char;
        } else if (!inQuotes && char === ')') {
          depth--;
          current += char;
        } else if (!inQuotes && char === ',' && depth === 0) {
          if (current.trim()) {
            args.push(this.parseComplexArgument(current.trim()));
          }
          current = '';
        } else {
          current += char;
        }
        
        i++;
      }
      
      if (current.trim()) {
        args.push(this.parseComplexArgument(current.trim()));
      }
      
      if (shouldLog) {
        console.log('Parsed arguments:', args);
      }
      return args;
    },

    parseComplexArgument(arg) {
      console.log('🔧 Parsing argument:', arg);
      
      // Handle quoted strings
      if ((arg.startsWith('"') && arg.endsWith('"')) || 
          (arg.startsWith("'") && arg.endsWith("'"))) {
        let unquoted = arg.slice(1, -1);
        
        // Only apply transformations if the string actually contains escape sequences or entities
        let processed = unquoted;
        
        // Check if we need HTML entity decoding (contains & or Unicode escapes)
        if (processed.includes('&') || processed.includes('\\u')) {
          processed = this.decodeHtmlEntities(processed);
        }
        
        // Check if we need escape sequence processing (contains backslashes)
        if (processed.includes('\\')) {
          processed = this.unescapeString(processed);
        }
        
        console.log('🔧 Argument processed:', unquoted, '→', processed);
        return processed;
      }
      
      // Handle numbers
      if (!isNaN(arg) && arg !== '') {
        return Number(arg);
      }
      
      // Handle booleans and special values
      if (arg === 'true') return true;
      if (arg === 'false') return false;
      if (arg === 'null') return null;
      if (arg === 'undefined') return undefined;
      
      // Return as-is for variable names and other identifiers
      return arg;
    },

    decodeHtmlEntities(str) {
      if (typeof str !== 'string') return str;
      
      // Log for debugging HTML entity issues
      const hasEntities = str.includes('&') || str.includes('\\u');
      if (hasEntities) {
        console.log('🔧 Decoding HTML entities:', str.substring(0, 100) + (str.length > 100 ? '...' : ''));
      }
      
      try {
        const textarea = document.createElement('textarea');
        textarea.innerHTML = str;
        const result = textarea.value;
        
        if (hasEntities) {
          console.log('🔧 Decoded result:', result.substring(0, 100) + (result.length > 100 ? '...' : ''));
        }
        
        return result;
      } catch (error) {
        console.warn('Error decoding HTML entities:', error);
        return str; // Return original string if decoding fails
      }
    },

    unescapeString(str) {
      if (typeof str !== 'string') return str;
      
      // Log for debugging escape sequence issues
      const hasEscapes = str.includes('\\');
      if (hasEscapes) {
        console.log('🔧 Unescaping string:', str.substring(0, 100) + (str.length > 100 ? '...' : ''));
      }
      
      let result = str;
      
      // Handle Unicode escape sequences first (before other backslash replacements)
      result = result.replace(/\\u([0-9a-fA-F]{4})/g, (match, hex) => {
        return String.fromCharCode(parseInt(hex, 16));
      });
      
      // Handle hex escape sequences
      result = result.replace(/\\x([0-9a-fA-F]{2})/g, (match, hex) => {
        return String.fromCharCode(parseInt(hex, 16));
      });
      
      // Handle common escape sequences
      result = result
        .replace(/\\n/g, '\n')      // newline
        .replace(/\\r/g, '\r')      // carriage return
        .replace(/\\t/g, '\t')      // tab
        .replace(/\\b/g, '\b')      // backspace
        .replace(/\\f/g, '\f')      // form feed
        .replace(/\\v/g, '\v')      // vertical tab
        .replace(/\\0/g, '\0')      // null character
        .replace(/\\'/g, "'")       // single quote
        .replace(/\\"/g, '"')       // double quote
        .replace(/\\\//g, '/')      // forward slash (JSON escape)
        .replace(/\\\\/g, '\\');    // backslash (must be last)
      
      if (hasEscapes) {
        console.log('🔧 Unescaped result:', result.substring(0, 100) + (result.length > 100 ? '...' : ''));
      }
      
      return result;
    },

    setupDynamicContent() {
      // Set up event listeners for dynamic content
      this.$nextTick(() => {
        const contentEl = this.$refs.dynamicContent;
        if (!contentEl) return;
        
        console.log('Setting up dynamic content, element:', contentEl);
        
        // Clean up old event listeners first
        const oldClickableElements = contentEl.querySelectorAll('[data-click-bound="true"]');
        oldClickableElements.forEach(el => {
          el.removeAttribute('data-click-bound');
          // Clone and replace to remove all event listeners
          const newEl = el.cloneNode(true);
          el.parentNode.replaceChild(newEl, el);
        });
        
        // Find and bind click events
        const clickableElements = contentEl.querySelectorAll('[onclick], [data-click]');
        console.log('Found clickable elements:', clickableElements.length);
        
        clickableElements.forEach(el => {
          const clickHandler = el.getAttribute('onclick') || el.getAttribute('data-click');
          if (clickHandler && !el.getAttribute('data-click-bound')) {
            console.log('Binding click handler:', clickHandler);
            
            el.setAttribute('data-click-bound', 'true');
            el.addEventListener('click', (e) => {
              e.preventDefault();
              e.stopPropagation();
              this.handleDynamicClick(clickHandler, e);
            });
          }
        });
        
        // Run any custom initialization
        this.htmlLoadedCustom();
      });
    },

    handleDynamicClick(handlerString, event) {
      // Handle click events from dynamic content
      try {
        console.log('Handling dynamic click:', handlerString);
        
        // Handle multiple statements separated by semicolons
        if (handlerString.includes(';')) {
          const statements = handlerString.split(';').map(s => s.trim()).filter(s => s);
          console.log('Processing multiple statements:', statements);
          
          for (const statement of statements) {
            this.handleDynamicClick(statement, event); // Recursively handle each statement
          }
          return;
        }
        
        // Handle direct variable assignments like "textfield2='Hello'" or "textfield2 = 'Hello'"
        const assignmentRegex = /^\s*(\w+)\s*=\s*(.+)\s*$/;
        const assignmentMatch = handlerString.match(assignmentRegex);
        
        if (assignmentMatch) {
          const [, varName, valueExpression] = assignmentMatch;
          
          // Evaluate the right-hand side expression
          const context = {
            ...this.$data,
            updateProperty: this.updateProperty,
            refreshAnimation: this.refreshAnimation,
            setAnimationPreset: this.setAnimationPreset,
            getCustomVariable: this.getCustomVariable,
            setCustomVariable: this.setCustomVariable,
            getCustomVariableDelayed: this.getCustomVariableDelayed,
            // Add webhook and validation methods
            triggerWebhook: this.triggerWebhook,
            isValidEmail: this.isValidEmail,
            Date: Date,
            moment: window.moment || (() => new Date()),
            nl2br: this.nl2br,
            translateField: this.translateField,
            translate: this.translate
          };
          
          // Use the utility function for evaluation
          const value = this.evaluateExpression(valueExpression.trim(), context);
          
          if (this.$data.hasOwnProperty(varName)) {
            console.log('Setting variable via assignment:', varName, '=', value);
            this[varName] = value;
            this.contentKey += 1; // Trigger content update without animation
            return;
          } else {
            console.warn(`Variable '${varName}' not found in component data`);
          }
        }
        
        // Parse method calls like "updateProperty('field', 'value')"
        const methodCallRegex = /(\w+)\s*\(\s*([^)]*)\s*\)/;
        const match = handlerString.match(methodCallRegex);
        
        if (match) {
          const [, methodName, argsString] = match;
          
          if (this[methodName] && typeof this[methodName] === 'function') {
            // Use the utility function for argument parsing
            const args = this.parseComplexArguments(argsString);
            
            console.log('Calling method:', methodName, 'with args:', args);
            
            // Call the method - don't increment contentKey here as this is during evaluation
            const result = this[methodName](...args);
            
            return result;
          }
        }
        
        // Legacy support: If no method match, try to evaluate as a simple expression
        // This handles cases like "textfield = 'new value'" (fallback)
        if (handlerString.includes('=')) {
          const [left, right] = handlerString.split('=').map(s => s.trim());
          const value = right.replace(/['"]/g, ''); // Remove quotes
          
          if (this.$data.hasOwnProperty(left)) {
            console.log('Setting property (legacy):', left, 'to:', value);
            this[left] = value;
            this.contentKey += 1; // Trigger content update without animation
          }
        }
      } catch (error) {
        console.error('Error handling dynamic click:', error);
      }
    }
  },

  render() {
    // Get the compile function using our robust method
    const compileFunction = getCompileFunction();
    
    // Add this for debugging in production
    console.log('CustomPluginElement render() called', {
      hasHtml: !!this.html,
      isLoading: this.isLoading,
      contentKey: this.contentKey,
      animationKey: this.animationKey
    });
    
    // Prevent infinite render loops
    if (this._renderCount === undefined) {
      this._renderCount = 0;
    }
    this._renderCount++;
    
    if (this._renderCount > 10) {
      console.error('Infinite render loop detected! Stopping renders.');
      return h('div', { style: { color: 'red', padding: '20px' } }, 'Render loop detected - check console');
    }
    
    // Reset render count after a delay
    setTimeout(() => {
      this._renderCount = 0;
    }, 1000);

    // Show loading state with MotionLoader
    if (!this.html || this.isLoading) {
      console.log('Showing loader - no HTML or isLoading=true');
      return h(AnimatePresence, { mode: 'wait' }, [
        h(MotionLoader, {
          key: 'loader',
          variant: this.loaderVariant || 'dots',
          size: this.loaderSize || 'md',
          color: 'text-gray-400',
          showText: false
        })
      ]);
    }

    // Create the template renderer using our utility
    const renderTemplate = createTemplateRenderer(this);
    
    // Create context for template rendering
    const context = {
      ...this.$data,
      // Add common methods
      updateProperty: this.updateProperty,
      refreshAnimation: this.refreshAnimation,
      setAnimationPreset: this.setAnimationPreset,
      getCustomVariable: this.getCustomVariable,
      setCustomVariable: this.setCustomVariable,
      getCustomVariableDelayed: this.getCustomVariableDelayed,
      // Add webhook and validation methods
      triggerWebhook: this.triggerWebhook,
      isValidEmail: this.isValidEmail,
      // Add date/time helpers
      Date: Date,
      moment: window.moment || (() => new Date()),
      // Add utility functions
      nl2br: this.nl2br,
      translateField: this.translateField,
      translate: this.translate
    };

    try {
      console.log('✅ Using shared template renderer');
      return h(AnimatePresence, { mode: 'wait' }, [
        h(motion.div, {
          key: this.stableContentKey,
          initial: this.initialAnimationVariants.initial,
          animate: this.initialAnimationVariants.animate,
          exit: this.initialAnimationVariants.exit,
          transition: this.initialAnimationVariants.transition,
          layout: false, // Disable layout animations to prevent jumping
          style: {
            willChange: this.hasInitiallyLoaded ? 'auto' : 'transform, opacity, filter'
          },
          onAnimationComplete: () => {
            if (!this.hasInitiallyLoaded) {
              this.hasInitiallyLoaded = true;
            }
          }
        }, [
          h('div', {
            key: `html-${this.contentKey}`, // Use contentKey for HTML updates
            innerHTML: this.processedHtml,
            ref: 'dynamicContent',
            onVnodeMounted: () => {
              this.$nextTick(() => {
                this.setupDynamicContent();
              });
            },
            onVnodeUpdated: () => {
              this.$nextTick(() => {
                this.setupDynamicContent();
              });
            }
          })
        ])
      ]);
    } catch (error) {
      console.error('Error with shared template renderer:', error);
      console.error('Error stack:', error.stack);
      return h(motion.div, {
        key: 'error',
        initial: { opacity: 0, scale: 0.8 },
        animate: { opacity: 1, scale: 1 },
        transition: { type: 'spring', stiffness: 400, damping: 25 },
        style: {
          padding: '20px',
          borderRadius: '8px',
          background: 'linear-gradient(135deg, #ff6b6b, #ffa726)',
          color: 'white',
          textAlign: 'center',
          fontWeight: 'bold'
        }
      }, `Template rendering error: ${error.message}`);
    }
  },

  watch: {
    html: {
      handler(newHtml, oldHtml) {
        // Only trigger re-animation for significant HTML structure changes
        // Skip if this is just the initial load or a minor content update
        if (newHtml !== oldHtml && oldHtml !== undefined && oldHtml !== null) {
          // Check if this is a substantial HTML structure change, not just content updates
          const isStructuralChange = this.isSignificantHtmlChange(oldHtml, newHtml);
          if (isStructuralChange) {
            console.log('Detected significant HTML structure change, triggering re-animation');
            this.refreshAnimation();
          }
        }
      },
      immediate: false
    },

    // Watch all reactive data properties to ensure re-rendering
    textfield: {
      handler() {
        console.log('textfield changed, updating content');
        this.contentKey += 1;
        this.$nextTick(() => {
          this.setupDynamicContent();
        });
      }
    },
    textfield2: {
      handler() {
        console.log('textfield2 changed, updating content');
        this.contentKey += 1;
        this.$nextTick(() => {
          this.setupDynamicContent();
        });
      }
    },
    textfield3: {
      handler() {
        this.contentKey += 1;
        this.$nextTick(() => {
          this.setupDynamicContent();
        });
      }
    },
    textfield4: {
      handler() {
        this.contentKey += 1;
        this.$nextTick(() => {
          this.setupDynamicContent();
        });
      }
    },
    textfield5: {
      handler() {
        this.contentKey += 1;
        this.$nextTick(() => {
          this.setupDynamicContent();
        });
      }
    },
    textfield6: {
      handler() {
        this.contentKey += 1;
        this.$nextTick(() => {
          this.setupDynamicContent();
        });
      }
    },
    textfield7: {
      handler() {
        this.contentKey += 1;
        this.$nextTick(() => {
          this.setupDynamicContent();
        });
      }
    },

    // Watch custom variables
    customVariables: {
      handler() {
        this.contentKey += 1;
        this.$nextTick(() => {
          this.setupDynamicContent();
        });
      },
      deep: true
    }
  },

  mounted() {
    // Test the compile function to ensure it's working
    testCompileFunction();
    
    // Add a slight delay for the initial animation to feel more natural
    setTimeout(() => {
      this.$nextTick(() => {
        this.htmlLoadedCustom();
      });
    }, 50);
  },

  updated() {
    this.$nextTick(() => {
      this.htmlLoadedCustom();
    });
  }
});
</script>

<style scoped>
.terms_html a {
  text-decoration: underline;
}

.loader,
.loader:after {
  border-radius: 50%;
  width: 10em;
  height: 10em;
}

.loader {
  margin: 60px auto;
  font-size: 10px;
  position: relative;
  text-indent: -9999em;
  border-top: 1.1em solid rgba(194, 188, 188, 0.2);
  border-right: 1.1em solid rgba(194, 188, 188, 0.2);
  border-bottom: 1.1em solid rgba(194, 188, 188, 0.2);
  border-left: 1.1em solid #c2bcbc;
  transform: translateZ(0);
  animation: load8 1.1s infinite linear;
}

@keyframes load8 {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}
</style>
